/**
 * A catalog category (backend #682 decision 3 / backend #688).
 *
 * **`shortName` is the stable KEY and `description` the resolver's German input; neither is a
 * display name any more.** They used to be the only thing there was, which is why the buyer saw
 * „Buecher", „Moebel" and „WerkzeugeMaschinen" in the category filter — in both languages, since
 * an ASCII key has none. `name` is resolved by the backend for the `Accept-Language` this app
 * sends, so rendering it is all a client has to do.
 *
 * Keep using `shortName` / `id` where a value is a value: a filter parameter, a
 * resolution-vocabulary target (the backend 400s anything but a known key), a lookup.
 */
export class ProductCategory {
    id: string;
    /** The stable key — a value, never a label. */
    shortName: string;
    /** The resolver's German input. Not a label either. */
    description: string;
    /**
     * The display name, resolved for the request's language. Optional so a pre-#688 backend
     * still type-checks; {@link productCategoryLabel} is what handles it being absent.
     */
    name?: string;
    /** Every translation of the name, for a future curation grid: `{ "de": "Bücher", … }`. */
    translations?: Record<string, string>;
}

/**
 * What to show for a category.
 *
 * Falls back to the key when the backend sent no resolved name — a deployment where this repo is
 * ahead of the backend shows what it showed before rather than an empty dropdown. That fallback
 * is the ONLY place a key may reach the screen.
 */
export function productCategoryLabel(
  category: Pick<ProductCategory, 'name' | 'shortName'> | null | undefined,
): string {
  return category?.name?.trim() || category?.shortName || '';
}
